import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppointmentModal } from "@/contexts/AppointmentModalContext";
import {
  calculatePaymentSection,
  getPaymentStatusLabelByAmountI18n,
} from "@/lib/appointment/utils";
import { getPrepaidLabelI18n, getSaleBillPrintedLabelI18n } from "@/lib/utils";
import { useRecordAbonements } from "@/hooks/modal/useRecordAbonements";
import { currentUses } from "@/lib/appointment/abonements";

/**
 * Computes the payment section fields dynamically from the current appointment state.
 * Returns null if appointment is not loaded.
 */
export function useComputedPayment() {
  const { t } = useTranslation();
  const { appointment } = useAppointmentModal();
  // Lines a client package pays owe no money: the admin's choice in this modal,
  // else what the backend has (abonements.ts).
  const serviceIds = (Array.isArray(appointment?.services) ? appointment!.services : []).map((s: any) => Number(s.id));
  const { data: packages } = useRecordAbonements(appointment?.id, serviceIds);
  const uses = currentUses((appointment?.paymentSection as any)?.abonementUses, packages);

  return useMemo(() => {
    if (!appointment) return null;
    const services = Array.isArray(appointment.services) ? appointment.services : [];
    const paymentCalc = calculatePaymentSection(appointment, services, uses);

    return {
      // Align statusLabel with isPaidFull logic in payment sections:
      // paidFull = 1 or 2 (or isPaid = true) means "Paid"
      statusLabel: (appointment.paymentSection?.paidFull === 1 ||
        appointment.paymentSection?.paidFull === 2 ||
        appointment.paymentSection?.isPaid === true)
        ? t("appointment.payment.paid", "Paid")
        : getPaymentStatusLabelByAmountI18n(paymentCalc.totalPaid, paymentCalc.totalAmount, t),
      prepaidLabel: getPrepaidLabelI18n(
        appointment.prepaid_status,
        appointment.detailsSection && typeof appointment.detailsSection.prepaid === "string"
          ? appointment.detailsSection.prepaid
          : undefined,
        t
      ),
      saleBillPrintedLabel: getSaleBillPrintedLabelI18n(
        appointment.is_sale_bill_printed,
        appointment.detailsSection && typeof appointment.detailsSection.saleBillPrinted === "string"
          ? appointment.detailsSection.saleBillPrinted
          : undefined,
        t
      ),
      totalAmount: paymentCalc.totalAmount,
      totalAmountString: paymentCalc.totalAmountString,
      totalPaid: paymentCalc.totalPaid,
      totalPaidString: paymentCalc.totalPaidString,
      totalRemaining: paymentCalc.totalRemaining,
      totalRemainingString: paymentCalc.totalRemainingString,
    };
  }, [appointment, appointment?.services, uses, t]);
}
